// Shared loan-report engine — slide-in preview drawer rendering the
// server-generated report PDF in an iframe (same approach as the portal
// MaterialPreviewDrawer), built over the shared ResourceDrawer (which
// owns Escape-to-dismiss). Footer drives the Draft → Certified → Filed
// ladder: "Certify & file" runs both mutations sequentially.

import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { useAuth } from "@/hooks/useAuth";
import { extractErrorMessage } from "@/api/client";
import { loanReportsApi } from "@/api/portfolioAdmin/loanReportsApi";
import type { LoanReportDto } from "@/api/portfolioAdmin/loanReportsApi";
import {
  useCertifyLoanReport,
  useFileLoanReport,
} from "@/hooks/portfolioAdmin/useLoanReports";
import { ResourceDrawer } from "../common/ResourceDrawer";
import { formatDate } from "../common/colorBand";
import { ReportPdfFrame } from "./ReportPdfFrame";
import {
  disciplineLabel,
  formatPeriod,
  LoanReportStatusBadge,
} from "./LoanReportLedger";

interface LoanReportPreviewDrawerProps {
  loanId: string;
  report: LoanReportDto;
  open: boolean;
  onClose: () => void;
  /** B6 QA tab override — certify/file through the QA-scoped endpoints
   *  (gated Compliance.QaReview) instead of the default Loans.Manage
   *  mutations. The drawer chrome and ladder stay identical. */
  actions?: {
    canAct: boolean;
    certify: (reportId: string) => Promise<unknown>;
    file: (reportId: string) => Promise<unknown>;
    isPending: boolean;
  };
}

export function LoanReportPreviewDrawer({
  loanId,
  report,
  open,
  onClose,
  actions,
}: LoanReportPreviewDrawerProps) {
  const auth = useAuth();
  const canManage = actions ? actions.canAct : auth.hasPermission("Loans.Manage");
  const toast = useToast();

  const certifyDefault = useCertifyLoanReport(loanId, report.discipline);
  const fileDefault = useFileLoanReport(loanId, report.discipline);
  const certifyFn = actions
    ? actions.certify
    : (id: string) => certifyDefault.mutateAsync(id);
  const fileFn = actions
    ? actions.file
    : (id: string) => fileDefault.mutateAsync(id);
  const pending = actions
    ? actions.isPending
    : certifyDefault.isPending || fileDefault.isPending;
  const [actionError, setActionError] = useState<string | null>(null);
  // Certify/File stay disabled until the PDF preview has actually rendered
  // — nobody certifies a report whose preview never appeared.
  const [previewReady, setPreviewReady] = useState(false);

  const certifyAndFile = async () => {
    setActionError(null);
    try {
      await certifyFn(report.id);
      await fileFn(report.id);
      toast.success("Report filed to Documents");
    } catch (err) {
      setActionError(extractErrorMessage(err));
    }
  };

  const fileOnly = async () => {
    setActionError(null);
    try {
      await fileFn(report.id);
      toast.success("Report filed to Documents");
    } catch (err) {
      setActionError(extractErrorMessage(err));
    }
  };

  const footer = (
    <>
      {actionError && (
        <p
          className="text-caption text-status-error m-0 mr-auto"
          role="alert"
          data-testid="loan-report-action-error"
        >
          {actionError}
        </p>
      )}
      <Button variant="secondary" onClick={onClose}>
        Close
      </Button>
      {report.status === "Draft" && canManage && (
        <Button
          variant="primary"
          onClick={() => void certifyAndFile()}
          loading={pending}
          disabled={!previewReady}
          data-testid="loan-report-certify-file"
        >
          Certify &amp; file
        </Button>
      )}
      {report.status === "Certified" && canManage && (
        <Button
          variant="primary"
          onClick={() => void fileOnly()}
          loading={pending}
          disabled={!previewReady}
          data-testid="loan-report-file"
        >
          File to Documents
        </Button>
      )}
      {report.status === "Filed" && (
        <span className="inline-flex items-center gap-space-3 text-small text-gray-mid">
          Filed {formatDate(report.filedAt)}
          {report.filedDocumentId && (
            <Link
              to={`/portfolio-admin/loans/${loanId}?tab=documents`}
              className="text-gold-shadow hover:underline underline-offset-4"
              data-testid="loan-report-filed-document-link"
            >
              View document
            </Link>
          )}
        </span>
      )}
    </>
  );

  return (
    <ResourceDrawer
      open={open}
      onClose={onClose}
      width="xl"
      title={
        report.discipline === "Qa"
          ? "QA review report"
          : `${disciplineLabel(report.discipline)} monthly report`
      }
      subtitle={formatPeriod(report.periodStart)}
      footer={footer}
      testId="loan-report-preview-drawer"
    >
      <div className="flex h-full flex-col gap-space-3">
        <div className="flex flex-wrap items-center gap-space-3">
          <LoanReportStatusBadge status={report.status} />
          <span className="text-caption text-gray-mid">
            Prepared by {report.preparedByUserName ?? "—"} ·{" "}
            {formatDate(report.preparedAt)}
          </span>
          {report.certifiedAt && (
            <span className="text-caption text-gray-mid">
              Certified by {report.certifiedByUserName ?? "—"} ·{" "}
              {formatDate(report.certifiedAt)}
            </span>
          )}
        </div>
        <ReportPdfFrame
          src={loanReportsApi.previewUrl(report.id)}
          onReadyChange={setPreviewReady}
          testId="loan-report-preview-iframe"
        />
      </div>
    </ResourceDrawer>
  );
}
